// src/components/ConfirmationDialog.tsx
/**
 * Custom Confirmation Dialog Component
 * 
 * Replaces browser confirm() dialogs with a more user-friendly interface
 * that matches the app's design system.
 */

import React from 'react';

interface ConfirmationDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /** Dialog title */
  title: string;
  /** Dialog message */
  message: string;
  /** Text for the confirm button */
  confirmText?: string;
  /** Text for the cancel button */
  cancelText?: string;
  /** Whether the action is destructive (affects button styling) */
  isDestructive?: boolean;
  /** Callback when confirmed */
  onConfirm: () => void;
  /** Callback when cancelled */
  onCancel: () => void;
  /** Additional CSS classes */
  className?: string;
}

export default function ConfirmationDialog({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
  onConfirm,
  onCancel,
  className = '',
}: ConfirmationDialogProps) {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onCancel();
    } else if (event.key === 'Enter') {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleCancel}
      />
      
      {/* Dialog */}
      <div 
        className={`
          relative bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700
          max-w-md w-full mx-4 p-6 transform transition-all duration-200 ease-out
          translate-y-16
          ${className}
        `}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        role="dialog"
        aria-labelledby="dialog-title"
        aria-describedby="dialog-message"
      >
        {/* Title */}
        <h3 
          id="dialog-title"
          className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3"
        >
          {title}
        </h3>
        
        {/* Message */}
        <p 
          id="dialog-message"
          className="text-sm text-gray-600 dark:text-gray-300 mb-6 leading-relaxed"
        >
          {message}
        </p>
        
        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={handleCancel}
            className="
              px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
              bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600
              rounded-md hover:bg-gray-200 dark:hover:bg-gray-600
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              transition-colors duration-150
            "
          >
            {cancelText}
          </button>
          
          <button
            onClick={handleConfirm}
            className={`
              px-4 py-2 text-sm font-medium text-white rounded-md
              focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-150
              ${isDestructive 
                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }
            `}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
} 